import { type AnyEventName, type EventPayload, type WebSDKEvents } from "@sumsub/websdk/types/types";
import { makeAutoObservable } from "mobx";
import { type AnalyticsManager } from "services/analytics/analyticsManager";
import type { FeatureStore } from "services/feature/featureStore";
import type { SumsubApplicationStore } from "services/sumsub/sumsubApplicationStore";
import { type SumsubClient } from "services/sumsub/sumsubClient";
import { SumsubCreateApplicationData } from "services/sumsub/sumsubTypes";
import { type SumsubToken } from "./types";

type DirectorIdentityWidgetState = "pending" | "success";

enum SumsubReviewAnswer {
  GREEN = "GREEN",
  RED = "RED",
}

export class DirectorIdentityWidgetStore {
  private _token: SumsubToken = undefined;
  private _state: DirectorIdentityWidgetState = "pending";

  constructor(
    private readonly _sumsubClient: SumsubClient,
    private readonly _featureStore: FeatureStore,
    private readonly _analyticsManager: AnalyticsManager,
    private readonly _sumsubApplicationStore: SumsubApplicationStore,
  ) {
    makeAutoObservable(this);
  }

  get token(): SumsubToken {
    return this._token;
  }

  private set token(value: SumsubToken) {
    this._token = value;
  }

  get state(): DirectorIdentityWidgetState {
    return this._state;
  }

  set state(value: DirectorIdentityWidgetState) {
    this._state = value;
  }

  async init(): Promise<void> {
    await this.getSumsubToken();
  }

  async getSumsubToken(): Promise<string> {
    const sumsubToken = await this._sumsubClient.getSumsubToken();
    this.token = sumsubToken;
    return sumsubToken;
  }

  async onMessage(message: AnyEventName, payload: EventPayload<AnyEventName>): Promise<void> {
    this._analyticsManager.event(`sumsub-message.${message}`, payload);

    if (this._featureStore.hasFeature("formationSumsubSdkV2")) {
      await this._onMessageV2(message, payload);
    } else {
      await this._onMessageV1(message, payload);
    }
  }

  async _onMessageV1(message: AnyEventName, payload: EventPayload<AnyEventName>): Promise<void> {
    this._analyticsManager.event(`sumsub-message-v1.${message}`, payload);

    switch (message) {
      case "idCheck.onApplicantLoaded":
        await this._onApplicantLoaded(payload as WebSDKEvents["idCheck.onApplicantLoaded"]);
        break;
      case "idCheck.applicantReviewComplete":
        // This event doesn't get triggered from the widget when SDKv2 enabled in the SS lvl
        await this._onApplicantReviewComplete();
        break;
    }
  }

  async _onMessageV2(message: AnyEventName, payload: EventPayload<AnyEventName>): Promise<void> {
    this._analyticsManager.event(`sumsub-message-v2.${message}`, payload);

    switch (message) {
      case "idCheck.onApplicantLoaded":
        await this._onApplicantLoaded(payload as WebSDKEvents["idCheck.onApplicantLoaded"]);
        break;
      case "idCheck.onApplicantStatusChanged":
        // This event works with SDKv1 and SDKv2 SS lvls
        await this._onApplicantStatusChanged(payload as WebSDKEvents["idCheck.onApplicantStatusChanged"]);
        break;
    }
  }

  private async _onApplicantLoaded(payload: WebSDKEvents["idCheck.onApplicantLoaded"]): Promise<void> {
    await this._sumsubClient.createSumsubApplication(new SumsubCreateApplicationData(payload.applicantId));
    this._analyticsManager.event("start-sumsub", payload);
  }

  private async _onApplicantReviewComplete(): Promise<void> {
    this.state = "success";
    this._sumsubApplicationStore.isPassedVerifications = true;
    this._analyticsManager.event("finish-sumsub");
  }

  private async _onApplicantStatusChanged(payload: WebSDKEvents["idCheck.onApplicantStatusChanged"]): Promise<void> {
    this._analyticsManager.event("sumsub-status-changed", payload);

    if (payload.reviewStatus !== "completed") {
      return;
    }

    // "reviewResult" attr actually exists for "completed" reviewStatus in at least several recent versions of the SDK
    // Could they forget to update schema?

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error - reviewResult actually exists for "completed" reviewStatus but not in SDK types
    const reviewResult = payload.reviewResult;

    if (!reviewResult) {
      this._analyticsManager.event("sumsub-error", { error: "no reviewResult on completed reviewStatus" });
      return;
    }

    switch (reviewResult.reviewAnswer) {
      case SumsubReviewAnswer.GREEN: // approved
        await this._onApplicantReviewComplete();
        break;
      case SumsubReviewAnswer.RED: // rejected
        this._analyticsManager.event("sumsub-rejected");
        break;
      default:
        this._analyticsManager.event("sumsub-error", {
          error: `invalid sumsub reviewResult.reviewAnswer: ${reviewResult.reviewAnswer}`,
        });
        break;
    }
  }
}
